import React from 'react'
import { mono } from './monolith.tokens'

/**
 * 네 방향 직선 — PERSONA §3(Sol LeWitt) / §4 "방향 해칭으로 면을 코딩한다".
 * 면의 정체를 색이 아니라 선 방향이 결정한다. 이 넷 말고는 쓰지 않는다.
 */
export type MonoLineDirection =
	| 'vertical'
	| 'rightDiagonal'
	| 'horizontal'
	| 'leftDiagonal'

const ANGLE: Record<MonoLineDirection, number> = {
	vertical: 0,
	rightDiagonal: 45,
	horizontal: 90,
	leftDiagonal: -45,
}

/** 면을 나누는 방식. 이 셋이 조합 체계 전부다 — 나란히 · 반씩 · 겹쳐서. */
export type MonoFigureLayout = 'bands' | 'halves' | 'stack'

interface PropsType {
	/**
	 * 페이지 안에서 유일한 값. SVG `<pattern>` id 의 접두사가 된다.
	 * `useId` 를 쓰지 않는 이유: 사전 렌더(prerender)와 클라이언트에서 값이 갈리면 안 되고,
	 * 생성되는 id 에 `:` 가 들어가 XML id 로 쓰기에 부적합하다.
	 */
	id: string
	layout: MonoFigureLayout
	/** 칸(bands·halves)마다, 또는 겹칠 층(stack)마다 배정할 방향 */
	directions: MonoLineDirection[]
	/** 한 변 길이(px) */
	size?: number
	/** 선 간격(px). 도형이 작아져도 이 값은 바꾸지 않는다 — 밀도가 곧 톤이다 */
	gap?: number
	className?: string
}

/**
 * LeWitt 의 4방향 직선 시스템으로 그린 2D 도형.
 *
 * 회색을 칠하지 않는다. 검은 선 1px 을 같은 간격으로 반복해서 톤을 만든다(박서보 — 같은 획의
 * 반복이 그대로 표면). 그래서 도형 크기를 줄일 때 `gap` 을 같이 줄이지 않는다: 선 간격은 절대값이고
 * 면의 크기만 달라진다. SVG 전체를 축소하면 선이 1px 밑으로 내려가 흐려진다.
 *
 * 테두리를 두르지 않는다 — 면의 경계는 선이 끝나는 자리가 정한다. 그래서 칸을 나눈 자리에만
 * 선을 긋는다(그 선은 경계가 아니라 배정이 바뀌는 지점이다).
 *
 * 캡션·해설을 붙이지 않는다(PERSONA §4). 장식이 아니라 면을 코딩하는 장치이므로
 * 스크린리더에서는 숨긴다.
 *
 * 주의 — 이것은 §4 가 규정한 **시그니처 도형(아이소메트릭·양안정)이 아니다.** 평면이고 반전이 없다.
 * 같은 작가에서 나왔지만 층위가 다른 어휘다.
 */
export const LewittFigure = (props: PropsType) => {
	const size = props.size ?? 80
	const gap = props.gap ?? 5

	const used = Array.from(new Set(props.directions))
	const patternId = (aDirection: MonoLineDirection) => `${props.id}-${aDirection}`

	const cells = React.useMemo(() => {
		if (props.layout === 'stack') {
			return props.directions.map(aDirection => ({
				x: 0,
				width: size,
				direction: aDirection,
			}))
		}
		const count = props.layout === 'halves' ? 2 : props.directions.length
		const width = size / count
		return props.directions.slice(0, count).map((aDirection, index) => ({
			x: index * width,
			width,
			direction: aDirection,
		}))
	}, [props.layout, props.directions, size])

	return (
		<svg
			className={props.className}
			width={size}
			height={size}
			viewBox={`-0.5 -0.5 ${size + 1} ${size + 1}`}
			aria-hidden="true"
			focusable="false"
			style={{ display: 'block' }}
		>
			<defs>
				{used.map(aDirection => (
					<pattern
						key={aDirection}
						id={patternId(aDirection)}
						width={gap}
						height={gap}
						patternUnits="userSpaceOnUse"
						patternTransform={`rotate(${ANGLE[aDirection]})`}
					>
						<line x1={0} y1={0} x2={0} y2={gap} stroke={mono.ink} strokeWidth={1} />
					</pattern>
				))}
			</defs>

			{cells.map((aCell, index) => (
				<rect
					key={`${aCell.direction}-${index}`}
					x={aCell.x}
					y={0}
					width={aCell.width}
					height={size}
					fill={`url(#${patternId(aCell.direction)})`}
				/>
			))}

			{/* 칸 경계 — 방향이 같은 칸끼리는 선이 이어지므로 나눈 자리를 명시한다 */}
			{props.layout !== 'stack' &&
				cells
					.slice(1)
					.map((aCell, index) => (
						<line
							key={`edge-${index}`}
							x1={aCell.x}
							y1={0}
							x2={aCell.x}
							y2={size}
							stroke={mono.ink}
							strokeWidth={1}
						/>
					))}
		</svg>
	)
}

interface HatchFillPropsType {
	/** 페이지 안에서 유일한 값. SVG `<pattern>` id 가 된다 (`LewittFigure` 와 같은 이유로 필수) */
	id: string
	direction?: MonoLineDirection
	/** 선 사이 간격(px) */
	gap?: number
	/** 색 파동 한 주기에 들어가는 선 수. 클수록 넓게 굽이친다. 홀수면 1·2 교대가 타일 경계에서 뒤집혀 짝수로 올린다 */
	period?: number
	/** 한 줄 걸러 반복되는 두 굵기(px). 정수만 쓴다 — 소수 굵기는 얇은 선이 아니라 옅은 선이 된다 */
	thinWidth?: number
	thickWidth?: number
	/** 파동의 골과 마루 색 */
	colorFrom?: string
	colorTo?: string
	className?: string
}

/** `#rrggbb` 두 색을 t(0~1)로 섞는다. 선마다 색이 달라야 해서 CSS 그라디언트로는 대신할 수 없다. */
const mixHex = (from: string, to: string, t: number) => {
	const parse = (hex: string) =>
		[1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16))
	const [r1, g1, b1] = parse(from)
	const [r2, g2, b2] = parse(to)
	const channel = (a: number, b: number) =>
		Math.round(a + (b - a) * t)
			.toString(16)
			.padStart(2, '0')
	return `#${channel(r1, r2)}${channel(g1, g2)}${channel(b1, b2)}`
}

/**
 * 면을 채우는 해칭 — 굵기는 **1px·2px 교대로 고정**이고, **색만 파동친다**.
 *
 * 굵기 교대(고주파)가 결을 만들고, 색이 긴 코사인을 따라 옅어졌다 진해지며(저주파) 그 위에
 * 물결을 얹는다. 굵기를 건드리지 않는 이유: 소수 굵기는 얇게 그려지지 않고 한 픽셀을 옅게
 * 칠해서 색이 바랜 선이 되므로, 굵기로 파동을 만들면 결국 색 파동과 뒤섞인다.
 *
 * 왜 CSS 가 아니라 SVG 인가: `repeating-linear-gradient` 는 타일 하나를 되풀이하는 구조라
 * 선마다 색·굵기를 달리 줄 수 없다. 균일한 면이면 CSS 로 충분하다(`Home.page` 의 카드 해칭).
 * 값이 선마다 변하는 순간부터는 선을 하나씩 그려야 한다 — PERSONA §4 "제작은 SVG 코드로 한다".
 */
export const LewittHatchFill = (props: HatchFillPropsType) => {
	const gap = props.gap ?? 6
	const thinWidth = props.thinWidth ?? 1
	const thickWidth = props.thickWidth ?? 2
	const colorFrom = props.colorFrom ?? mono.line
	const colorTo = props.colorTo ?? mono.faint
	const angle = ANGLE[props.direction ?? 'leftDiagonal']

	const requestedPeriod = props.period ?? 28
	const period = requestedPeriod % 2 === 0 ? requestedPeriod : requestedPeriod + 1

	const lines = React.useMemo(
		() =>
			Array.from({ length: period }, (unused, index) => {
				const width = index % 2 === 0 ? thickWidth : thinWidth
				const wave = 0.5 - 0.5 * Math.cos((2 * Math.PI * index) / period)
				return {
					x: Math.round(index * gap + gap / 2 - width / 2),
					width,
					color: mixHex(colorFrom, colorTo, wave),
				}
			}),
		[gap, period, thinWidth, thickWidth, colorFrom, colorTo],
	)

	return (
		<svg
			className={props.className}
			aria-hidden="true"
			focusable="false"
			width="100%"
			height="100%"
			preserveAspectRatio="none"
		>
			<defs>
				<pattern
					id={props.id}
					width={gap * period}
					height={gap}
					patternUnits="userSpaceOnUse"
					patternTransform={`rotate(${angle})`}
				>
					{lines.map((aLine, index) => (
						<rect
							key={index}
							x={aLine.x}
							y={0}
							width={aLine.width}
							height={gap}
							fill={aLine.color}
						/>
					))}
				</pattern>
			</defs>
			<rect width="100%" height="100%" fill={`url(#${props.id})`} />
		</svg>
	)
}
